import type { ProductDataType } from '@am3nd-org/pointcraft-types';
import { doc, onSnapshot, Unsubscribe, getFirestore, DocumentSnapshot } from 'firebase/firestore';

import { ApiMethods, apiSenderV2, apiEndpoints } from 'src/utils/axios';
import {
  InventoryLocalDB,
  getInventorySyncMeta,
  saveInventorySyncMeta,
  INVENTORY_CACHE_SCHEMA_VERSION,
} from 'src/utils/Services/DB_Utils';

import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { getFirebaseWebApp } from 'src/auth/store/firebase/lib';

// Prevents concurrent sync runs — a new Firestore event is dropped if one is already in flight.
let isSyncing = false;

function normalizeFirestoreDate(value: unknown): Date {
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  if (value instanceof Date) return value;
  return new Date(value as string);
}

function isActiveInventoryTenant(businessId: string): boolean {
  return useDeviceSyncStore.getState().businessData?.id === businessId;
}

async function runInventorySync(
  remoteVersion: number,
  remoteUpdatedAt: Date,
  businessId: string
): Promise<void> {
  if (isSyncing) return;
  if (!isActiveInventoryTenant(businessId)) return;
  // Hold off if the one-time chunk migration is still running.
  if (InventoryLocalDB.isMigrationInProgress()) return;

  isSyncing = true;

  try {
    const localMeta = await getInventorySyncMeta();

    const ownsCurrentCache =
      localMeta?.businessId === businessId &&
      localMeta.schemaVersion === INVENTORY_CACHE_SCHEMA_VERSION;

    if (ownsCurrentCache && localMeta.version === remoteVersion) {
      return;
    }

    if (!ownsCurrentCache) {
      await InventoryLocalDB.clearAll();
    }

    if (!ownsCurrentCache || !localMeta?.lastSyncedAt) {
      // First sync or corrupted state — fetch the full inventory.
      const result = await apiSenderV2(apiEndpoints.products.base, ApiMethods.GET, null);

      if (!result || !Array.isArray(result)) {
        throw new Error('[InventorySync] Full inventory fetch returned unexpected response');
      }
      if (!isActiveInventoryTenant(businessId)) return;

      const products = result as ProductDataType[];
      await InventoryLocalDB.saveAll(products);
    } else {
      // Incremental sync — only fetch items changed since last sync.
      const since = encodeURIComponent(localMeta.lastSyncedAt);
      const changes = await apiSenderV2(
        `${apiEndpoints.products.changes}?since=${since}`,
        ApiMethods.GET,
        null
      );

      if (!changes || typeof changes !== 'object') {
        throw new Error('[InventorySync] Inventory changes fetch returned unexpected response');
      }
      if (!isActiveInventoryTenant(businessId)) return;

      const updated: ProductDataType[] = changes.updated ?? [];
      const deleted: string[] = changes.deleted ?? [];

      // Write targeted changes to the inventory table instead of re-saving the entire catalog.
      await Promise.all([
        updated.length > 0 ? InventoryLocalDB.upsertMany(updated) : Promise.resolve(),
        ...deleted.map((id) => InventoryLocalDB.hardDelete(id)),
      ]);
    }

    if (!isActiveInventoryTenant(businessId)) {
      await InventoryLocalDB.clearAll();
      return;
    }

    // Only persist sync state after all changes are successfully applied.
    const recordCount = await InventoryLocalDB.count();
    await saveInventorySyncMeta({
      version: remoteVersion,
      lastSyncedAt: remoteUpdatedAt.toISOString(),
      businessId,
      schemaVersion: INVENTORY_CACHE_SCHEMA_VERSION,
      recordCount,
    });

    // Recalculate stats once after sync completes (covers both full and incremental paths).
    InventoryLocalDB.recalculateStats().catch((err) => {
      console.error('[InventorySync] Stats recalculation failed:', err);
    });
  } finally {
    isSyncing = false;
  }
}

export async function handleInventoryBusinessField(inventory: unknown): Promise<void> {
  if (!inventory || typeof inventory !== 'object') return;
  const inv = inventory as { version?: number; updatedAt?: unknown };
  if (!inv.version) return;
  const remoteVersion: number = inv.version;
  const remoteUpdatedAt: Date = normalizeFirestoreDate(inv.updatedAt);
  const businessId = useDeviceSyncStore.getState().businessData?.id;
  if (!businessId) return;
  runInventorySync(remoteVersion, remoteUpdatedAt, businessId).catch((err) => {
    console.error('[InventorySync] Sync failed:', err);
    isSyncing = false;
  });
}

export function startInventorySyncListener(businessId: string): Unsubscribe {
  if (!businessId) return () => {};

  const db = getFirestore(getFirebaseWebApp());
  const docRef = doc(db, 'business', businessId);

  return onSnapshot(
    docRef,
    (snapshot: DocumentSnapshot) => {
      if (!snapshot.exists()) return;

      const data = snapshot.data();
      const inventory = data?.inventory;

      if (!inventory?.version) return;

      const remoteVersion: number = inventory.version;
      const remoteUpdatedAt: Date = normalizeFirestoreDate(inventory.updatedAt);

      runInventorySync(remoteVersion, remoteUpdatedAt, businessId).catch((err) => {
        console.error('[InventorySync] Sync failed:', err);
        isSyncing = false;
      });
    },
    (err) => {
      console.error('[InventorySync] Listener error:', err);
    }
  );
}
